import { Unsubscribe as AuthUnsubscribe, onAuthStateChanged, signOut } from 'firebase/auth';
import { Unsubscribe as FireStoreUnsubscribe, onSnapshot } from 'firebase/firestore';
import React from 'react';
import { toast } from 'react-toastify';

import { FirebaseService } from '../Firebase/Firebase';
import { getAllProjects, getAllUsers, getDefaultOrganisation, getOrganisation, getUser } from '../Firebase/http';
import i18n, { applyLanguage, isLanguage } from '../i18n/i18n';
import {
  AuthenticatedUserDataType,
  CustomDocumentDataType,
  CustomDrawingDataType,
  CustomOrganisationDataType,
  CustomProjectDataType,
  CustomUserDataType,
  DataListViewMode,
  UserRole
} from '../Utils/types';

import { useDrawings } from './useDrawings';

const firebaseService = FirebaseService;

type ContextType = {
  user: AuthenticatedUserDataType | undefined;
  setUser: React.Dispatch<React.SetStateAction<AuthenticatedUserDataType | undefined>>;
  organisation: CustomOrganisationDataType | undefined;
  setOrganisation: React.Dispatch<React.SetStateAction<CustomOrganisationDataType | undefined>>;
  loginWithEmailAndPassword: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  data: CustomDocumentDataType[];
  dataLoading: boolean;
  firebaseService: typeof FirebaseService;
  filteredData: CustomDocumentDataType[] | null;
  setFilteredData: React.Dispatch<React.SetStateAction<CustomDocumentDataType[] | null>>;
  projects: CustomProjectDataType[];
  // The organisation's Drawings, live (OQ-161).
  drawings: CustomDrawingDataType[];
  drawingsLoading: boolean;
  organisationUsers: CustomUserDataType[];
  dataListMode: DataListViewMode;
  setDataListMode: React.Dispatch<React.SetStateAction<DataListViewMode>>;
  loginAttemptMessage: string;
  setLoginAttemptMessage: React.Dispatch<React.SetStateAction<string>>;
};
const initialCTX: ContextType = {
  user: undefined,
  setUser: () => {},
  organisation: undefined,
  setOrganisation: () => {},
  loginWithEmailAndPassword: async () => {},
  logout: async () => {},
  loading: true,
  data: [],
  dataLoading: true,
  firebaseService,
  filteredData: [],
  setFilteredData: () => {},
  projects: [],
  drawings: [],
  drawingsLoading: true,
  organisationUsers: [],
  dataListMode: 'FILES',
  setDataListMode: () => {},
  loginAttemptMessage: '',
  setLoginAttemptMessage: () => {}
};
const GlobalStateContext = React.createContext<ContextType>(initialCTX);

const GlobalState: React.FC<{ children: React.ReactNode }> = (props) => {
  const [user, setUser] = React.useState<AuthenticatedUserDataType>();
  const [organisation, setOrganisation] = React.useState<CustomOrganisationDataType>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [data, setData] = React.useState<CustomDocumentDataType[]>([]);
  const [dataLoading, setDataLoading] = React.useState<boolean>(true);
  const [filteredData, setFilteredData] = React.useState<CustomDocumentDataType[] | null>(null);
  const [projects, setProjects] = React.useState<CustomProjectDataType[]>([]);
  const { drawings, loading: drawingsLoading } = useDrawings(user ? organisation?.docId : undefined);
  const [organisationUsers, setOrganisationUsers] = React.useState<CustomUserDataType[]>([]);
  const [dataListMode, setDataListMode] = React.useState<DataListViewMode>(
    (localStorage.getItem('dataListMode') as DataListViewMode) ?? 'FILES'
  );
  const [loginAttemptMessage, setLoginAttemptMessage] = React.useState<string>('');

  // After sign-in the UI follows the user's language, missing meaning Swedish;
  // signing out keeps it (OQ-162, ui/app-views.md § Language).
  const userLanguage = user ? (isLanguage(user.language) ? user.language : 'sv') : undefined;
  React.useEffect(() => {
    if (userLanguage) void applyLanguage(userLanguage);
  }, [userLanguage]);

  React.useEffect(() => {
    // Guards against a stale onAuthStateChanged callback (e.g. a rapid
    // login-then-logout) resolving its async user/org fetch after a later
    // callback has already updated the state — without this, the stale
    // callback would overwrite the newer (possibly signed-out) state.
    let latestUid: string | undefined;
    const authUnsubscribe: AuthUnsubscribe = onAuthStateChanged(firebaseService.auth, async (u) => {
      latestUid = u?.uid;
      if (u) {
        try {
          const userId = u.uid;
          const _userData = await getUser(userId);
          if (latestUid !== userId) return;
          setUser({ credentials: u, ..._userData });
          let _orgData: CustomOrganisationDataType;
          if (!_userData.organisationId) {
            _orgData = await getDefaultOrganisation();
          } else {
            _orgData = await getOrganisation(_userData.organisationId);
          }
          if (latestUid !== userId) return;

          // Log the response to debug missing docId
          if (!_orgData?.docId) {
            console.warn('⚠️ GlobalState: Organisation data missing docId', {
              orgData: _orgData,
              hasDocId: !!_orgData?.docId,
              keys: _orgData ? Object.keys(_orgData) : []
            });
          }

          setOrganisation({ ..._orgData });
          if (_orgData?.docId) {
            firebaseService.updateMediaCollectionRef(_orgData.docId);
          } else {
            console.warn('⚠️ GlobalState: Cannot update media collection ref - docId is missing');
          }
        } catch (e) {
          console.log('GlobalState onAuthStateChanged failed:', e);
        }
      } else {
        // User is signed out
        setUser(undefined);
      }
      setLoading(false);
    });

    return () => {
      if (authUnsubscribe) authUnsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const _dataListMode = localStorage.getItem('dataListMode');
    if (_dataListMode !== dataListMode) localStorage.setItem('dataListMode', dataListMode);
  }, [dataListMode]);

  React.useEffect(() => {
    let mediaUnsubscribe: FireStoreUnsubscribe;
    setDataLoading(true);

    // TODO: Consider moving the data/collection fetching fully to Firebase.ts to improve consistency in the architecture OR remove/rename Firebase.ts

    if (user && organisation) {
      mediaUnsubscribe = onSnapshot(firebaseService.mediaCollectionRef, (snap) => {
        setData(
          snap.docs
            .map(
              (doc) =>
                ({
                  docId: doc.id,
                  docRef: doc.ref,
                  ...doc.data()
                }) as CustomDocumentDataType
            )
            .sort((a, b) => b.timestamp.toString().localeCompare(a.timestamp.toString()))
        );
        setDataLoading(false);
      });

      // Must fetch the projects by organisationId. The GlobalState projects should contain only the projects of the current organisation. As the files which are rendered on map view are only from current organisation.
      // The hook dependency will retrigger on the change of user/organisation
      getAllProjects({ params: { organisationId: organisation.docId } })
        .then((projects) => setProjects(projects))
        .catch((e) => console.log('fetching all projects failed:', e));
      if (user.role === UserRole.ADMIN || user.role === UserRole.SUPERADMIN)
        getAllUsers({ params: { organisationId: organisation.docId } })
          .then((users) => setOrganisationUsers(users))
          .catch((e) => console.log('fetching organisation users failed:', e));
    } else {
      // reset data on logout
      setData([]);
      setProjects([]);
      setFilteredData(null);
      setDataLoading(false);
    }

    return () => {
      if (mediaUnsubscribe) mediaUnsubscribe();
    };
  }, [organisation, user]);

  const loginWithEmailAndPassword = async (email: string, password: string) => {
    try {
      const userCredential = await firebaseService.signInWithEmailAndPassword(email, password);
      const customUserCredential = await getUser(userCredential.user.uid);
      const customOrganisation = customUserCredential.organisationId
        ? await getOrganisation(customUserCredential.organisationId)
        : await getDefaultOrganisation();

      if (customUserCredential)
        setUser({
          credentials: userCredential.user,
          ...customUserCredential
        });

      if (customOrganisation) {
        if (customOrganisation.docId) {
          firebaseService.updateMediaCollectionRef(customOrganisation.docId);
        }
        setOrganisation(customOrganisation);
      }
      setLoginAttemptMessage('');
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (code === 'auth/wrong-password') {
        setLoginAttemptMessage(i18n.t('shell.globalState.wrongPassword'));
      }
      if (code === 'auth/user-not-found') {
        setLoginAttemptMessage(i18n.t('shell.globalState.userNotFound'));
      }
      console.error('Email and password login failed', e);
      throw e;
    }
  };

  const logout = async () => {
    try {
      await signOut(firebaseService.auth);
      setData([]);
      setProjects([]);
      setFilteredData(null);
      setUser(undefined);
      setOrganisation(undefined);
    } catch (error) {
      toast.error(i18n.t('shell.globalState.signOutFailed'));
      console.log(error);
    }
  };

  const contextValue = React.useMemo(
    () => ({
      user,
      setUser,
      organisation,
      setOrganisation,
      loginWithEmailAndPassword,
      logout,
      loading,
      data,
      dataLoading,
      firebaseService,
      filteredData,
      setFilteredData,
      projects,
      drawings,
      drawingsLoading,
      organisationUsers,
      dataListMode,
      setDataListMode,
      loginAttemptMessage,
      setLoginAttemptMessage
    }),
    [
      user,
      organisation,
      loading,
      data,
      dataLoading,
      filteredData,
      projects,
      drawings,
      drawingsLoading,
      organisationUsers,
      dataListMode,
      loginAttemptMessage
    ]
  );

  return <GlobalStateContext.Provider value={contextValue}>{props.children}</GlobalStateContext.Provider>;
};

export default GlobalState;

export const useGlobalState = () => React.useContext(GlobalStateContext);
